// Classes previously in this file have been split into keycloak-auth.service.ts and auth.guard.ts for clarity and convention.

import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { AuthConfig, OAuthService } from 'angular-oauth2-oidc';
import { Observable, from } from 'rxjs';
import { switchMap } from 'rxjs/operators';

export interface Profile {
	username: string;
	email: string;
	initials: string;
}

@Injectable({
	providedIn: 'root',
})
export class AuthService {
	constructor(private oauthService: OAuthService, private router: Router) {}

	initAuth(authConfig: AuthConfig): Promise<boolean> {
		this.oauthService.configure(authConfig);
		this.oauthService.setupAutomaticSilentRefresh();
		return this.oauthService.loadDiscoveryDocumentAndTryLogin();
	}

	login(): void {
		this.oauthService.initLoginFlow();
	}

	logout(): void {
		this.oauthService.logOut();
	}

	isLoggedIn(): Promise<boolean> {
		return Promise.resolve(this.oauthService.hasValidAccessToken());
	}

	getToken(): Promise<string | undefined> {
		return Promise.resolve(this.oauthService.getAccessToken() || undefined);
	}

	getUserRoles(): string[] {
		const claims = this.oauthService.getIdentityClaims();
		if (!claims) return [];
		// Adjust this according to your OIDC provider's claim for roles
		return claims['roles'] || claims['role'] || [];
	}

	async loadUserProfile(): Promise<Profile> {
		const claims = this.oauthService.getIdentityClaims();
		if (!claims) throw new Error('No user profile loaded');
		const username = claims['name'] || claims['preferred_username'] || claims['sub'];
		const email = claims['email'] || '';
		const initials = username
			.split(' ')
			.map((p: string) => p[0])
			.join('')
			.toUpperCase();
		return { username, email, initials };
	}

	isTokenExpired(): boolean {
		// The OAuthService does not expose a direct isTokenExpired, but we can check token validity
		return !this.oauthService.hasValidAccessToken();
	}
}

@Injectable({
	providedIn: 'root',
})
export class AuthGuard {
	constructor(private router: Router, private authService: AuthService) {}

	canActivateChild(
		childRoute: ActivatedRouteSnapshot,
		state: RouterStateSnapshot,
	): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
		return from(this.authService.isLoggedIn()).pipe(
			switchMap((authenticated) => {
				let roles: string[] = [];
				if (authenticated) {
					roles = this.authService.getUserRoles();
				}
				return from(this.isAccessAllowed(childRoute, state, authenticated, roles));
			}),
		);
	}

	async isAccessAllowed(
		route: ActivatedRouteSnapshot,
		state: RouterStateSnapshot,
		authenticated: boolean,
		roles: string[],
	) {
		if (!authenticated) {
			this.authService.login();
			return false;
		}
		const requiredRoles = route.data.roles;
		if (!(requiredRoles instanceof Array) || requiredRoles.length === 0) {
			return true;
		}
		return requiredRoles.every((role: string) => roles.includes(role));
	}
}
